import React, { useEffect, useMemo, useRef } from 'react';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import { fetchChangelogAssetUrl } from 'src/services/changelog';
import styles from './SafeHtml.module.scss';

const PROTECTED_PREFIX = '/portal/';

/**
 * Renders backend-authored HTML after sanitising it. Images and videos that point
 * at a protected portal path are fetched with the bearer token and swapped for
 * object URLs, because a plain `src` cannot carry the Authorization header.
 * Links open in a new tab; iframes are never allowed here.
 */
const SafeHtml: React.FC<{ html: string; className?: string; onImageClick?: (src: string, alt: string) => void }> = ({
  html,
  className,
  onImageClick,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        FORBID_TAGS: ['style', 'iframe', 'form', 'input', 'script'],
        ADD_TAGS: ['video', 'source'],
        ADD_ATTR: ['controls', 'poster', 'preload'],
      }),
    [html],
  );

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const controller = new AbortController();
    const objectUrls: string[] = [];
    const media = Array.from(
      root.querySelectorAll<HTMLImageElement | HTMLVideoElement | HTMLSourceElement>('img, video, source'),
    );
    media.forEach(element => {
      const src = element.getAttribute('src') ?? '';
      if (!src.startsWith(PROTECTED_PREFIX)) return;
      element.removeAttribute('src');
      fetchChangelogAssetUrl(src, controller.signal)
        .then(url => {
          objectUrls.push(url);
          element.setAttribute('src', url);
          if (element instanceof HTMLSourceElement) element.parentElement?.dispatchEvent(new Event('load'));
        })
        .catch(() => {
          element.setAttribute('alt', element.getAttribute('alt') || '');
        });
    });
    return () => {
      controller.abort();
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [sanitized]);

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG' && onImageClick) {
      const img = target as HTMLImageElement;
      if (img.src) onImageClick(img.src, img.alt);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      ref={ref}
      className={classNames(styles.root, className)}
      onClick={onClick}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};

export default SafeHtml;
